/**
 * Projects List Page
 * 
 * Main page for displaying country-specific projects with list + map view.
 * This page is shown when accessing a country subdomain (e.g., peru.lotespro.com)
 */

import React, { useState, useEffect } from 'react';
import { useCountry } from '../../contexts/CountryContext';
import { fetchProjects } from '../../services/apiClient';
import Navigation from '../../components/tulote/Navigation';
import ProjectsList from './ProjectsList';
import ProjectsMap from './ProjectsMap';

function ProjectsListPage() {
    const { country, countryConfig, loading: countryLoading } = useCountry();

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectsBbox, setProjectsBbox] = useState(null);

    // Hover/selection state for map
    const [hoveredProjectId, setHoveredProjectId] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    
    // Mobile view state: 'list' or 'map'
    const [mobileView, setMobileView] = useState('list');

    // Fetch projects when country changes
    useEffect(() => {
        if (!country || countryLoading) return;

        const loadProjects = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetchProjects(country);
                
                // Ordenar proyectos: primero PUBLICADO, luego EDICION, y dentro de cada grupo por updated_at
                const sortedProjects = [...(response.data || [])].sort((a, b) => {
                    // Prioridad por status: PUBLICADO primero (0), EDICION después (1)
                    const statusPriority = {
                        'PUBLICADO': 0,
                        'EDICION': 1
                    };
                    const priorityA = statusPriority[a.status] ?? 2;
                    const priorityB = statusPriority[b.status] ?? 2;
                    
                    // Si tienen diferente prioridad, ordenar por prioridad
                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }
                    
                    // Si tienen la misma prioridad, ordenar por updated_at (más recientes primero)
                    const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
                    const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
                    return dateB - dateA; // Orden descendente (más recientes primero)
                });
                
                setProjects(sortedProjects);
                setProjectsBbox(response.bbox);
            } catch (err) {
                console.error('Error loading projects:', err);
                setError(err.message);
                setProjects([]);
                setProjectsBbox(null);
            } finally {
                setLoading(false);
            }
        };

        loadProjects();
    }, [country, countryLoading]);


    if (countryLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Detectando país...</p>
                </div>
            </div>
        );
    }

    if (!country || !countryConfig) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center p-8">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        No se pudo detectar el país
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Por favor, accede desde un subdominio válido.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-gray-100 dark:bg-gray-900 flex flex-col overflow-hidden">
            {/* Navigation Header */}
            <Navigation />

            {/* Mobile View Toggle - segmented control */}
            <div className="lg:hidden px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="inline-flex w-full p-1 rounded-lg bg-gray-100 dark:bg-gray-900">
                    <button
                        onClick={() => setMobileView('list')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                            mobileView === 'list'
                                ? 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Lista
                        <span className={`text-xs font-medium ${mobileView === 'list' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500'}`}>
                            {projects.length}
                        </span>
                    </button>
                    <button
                        onClick={() => setMobileView('map')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                            mobileView === 'map'
                                ? 'bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        Mapa
                    </button>
                </div>
            </div>

            {/* Main Content: Sidebar + Map */}
            <div className="flex-1 flex relative overflow-hidden">
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
                            <p className="text-gray-600 dark:text-gray-400">Cargando proyectos...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
                            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                Error al cargar proyectos
                            </h3>
                            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Reintentar
                            </button>
                        </div>
                    </div>
                ) : projects.length > 0 ? (
                    <>
                        {/* Sidebar - Projects List (scrollable) - Desktop always visible, Mobile conditional */}
                        <div className={`${mobileView === 'list' ? 'flex' : 'hidden'} lg:flex w-full lg:w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col h-full overflow-hidden`}>
                            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-xl leading-none">{countryConfig.flag}</span>
                                    <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white tracking-tight">
                                        Proyectos en {countryConfig.name}
                                    </h2>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    <span className="font-semibold text-gray-700 dark:text-gray-300">{projects.length}</span>
                                    {' '}{projects.length === 1 ? 'proyecto disponible' : 'proyectos disponibles'}
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-3 sm:p-4">
                                    <ProjectsList
                                        projects={projects}
                                        hoveredProjectId={hoveredProjectId}
                                        selectedProjectId={selectedProjectId}
                                        onHover={setHoveredProjectId}
                                        onSelect={setSelectedProjectId}
                                        countryConfig={countryConfig}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Map - Takes remaining space (no scroll) - Desktop always visible, Mobile conditional */}
                        <div className={`${mobileView === 'map' ? 'flex' : 'hidden'} lg:flex flex-1 relative overflow-hidden`} style={{ height: '100%', minHeight: 0 }}>
                            <ProjectsMap
                                key={`map-${country}-${projects.length}`}
                                projects={projects}
                                hoveredProjectId={hoveredProjectId}
                                selectedProjectId={selectedProjectId}
                                onHover={setHoveredProjectId}
                                onSelect={setSelectedProjectId}
                                countryConfig={countryConfig}
                                countryCode={country}
                                projectsBbox={projectsBbox}
                            />
                        </div>
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="text-center p-8">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                No hay proyectos disponibles
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                No se encontraron proyectos publicados para este país.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProjectsListPage;
